import React from 'react';
import { X, ExternalLink, Gamepad2 } from 'lucide-react';
import { basePrefix } from '../../utils/format';

// In-app Play: the real Roblox game page, proxied through /api/roblox-page
// (frame blockers stripped server-side), rendered in an iframe so players
// never leave the directory. Falls back to the external Roblox page.
export const PlayModal: React.FC<{
  placeId: string;
  title: string;
  robloxUrl: string;
  onClose: () => void;
}> = ({ placeId, title, robloxUrl, onClose }) => {
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-black/85 backdrop-blur-sm p-2 sm:p-4">
      <div className="flex items-center justify-between gap-2 max-w-6xl w-full mx-auto px-1 py-2">
        <p className="flex items-center gap-2 text-sm font-bold text-white truncate">
          <Gamepad2 className="h-4 w-4 text-red-400 shrink-0" />
          <span className="truncate">{title}</span>
        </p>
        <div className="flex items-center gap-2 shrink-0">
          <a
            href={robloxUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-500 hover:bg-red-400 text-white text-xs font-bold transition-all"
          >
            <ExternalLink className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Open in Roblox</span>
            <span className="sm:hidden">Roblox</span>
          </a>
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-slate-300 hover:text-white hover:bg-white/10 transition-all"
            aria-label="Close player"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>
      <div className="flex-1 max-w-6xl w-full mx-auto rounded-xl overflow-hidden border border-white/10 bg-[#0d111a] min-h-0">
        <iframe
          src={`${basePrefix()}/api/roblox-page/${placeId}`}
          title={`Play ${title}`}
          className="h-full w-full border-0 bg-white"
          allow="fullscreen; autoplay; gamepad; xr-spatial-tracking"
          referrerPolicy="no-referrer"
        />
      </div>
      <p className="text-center text-[11px] font-mono text-slate-500 py-2">
        Sign in with your Roblox account inside the player. If it stays blank, use “Open in Roblox”.
      </p>
    </div>
  );
};
